<template>
  <div class="reseller-products">
    <v-container fluid class="pa-3 pa-md-6">
      <div class="hero">
        <div class="hero-content">
          <div class="hero-badge">Marketplace Catalog</div>
          <h1 class="hero-title">Reseller Products</h1>
          <p class="hero-subtitle">
            Pick verified seller products and start earning on every resale.
          </p>
        </div>
        <div class="hero-glow"></div>
      </div>

      <!-- Tabs : Catalogue / Mon catalogue -->
      <v-tabs v-model="tab" color="indigo" class="catalog-tabs">
        <v-tab href="#catalog">
          <v-icon left small>mdi-storefront-outline</v-icon>
          Available Products
        </v-tab>
        <v-tab href="#mine">
          <v-icon left small>mdi-package-variant</v-icon>
          My Catalog
          <v-chip x-small v-if="myProducts.length" class="ml-1" color="indigo" dark>{{ myProducts.length }}</v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <!-- Catalogue disponible -->
        <v-tab-item id="catalog" value="catalog">
          <div class="catalog-section">
            <v-card class="table-card">
              <v-card-title class="table-title">
                <div>
                  <div class="title-text">Products available to resell</div>
                  <div class="subtitle-text">Select products to add to your reseller catalog</div>
                </div>
                <v-spacer />
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search products"
                  single-line
                  hide-details
                  dense
                  class="search-field"
                />
              </v-card-title>

              <v-alert v-if="catalogError" type="error" dense class="mx-4 mt-2 mb-0">
                {{ catalogError }}
              </v-alert>
              <v-progress-linear v-if="catalogLoading" indeterminate color="indigo" height="3" />

              <div class="catalog-grid">
                <div v-for="item in filteredCatalog" :key="item.id" class="catalog-item">
                  <a class="cat-card" @click.prevent="$router.push('/market_place/items/' + item.id)">
                    <div class="cat-media">
                      <img
                        :src="item.image || '/placeholder-product.png'"
                        :alt="item.name"
                        loading="lazy"
                      />
                      <span class="cat-stock">
                        <i class="fas fa-box"></i>
                        {{ item.stock }}
                      </span>
                    </div>

                    <div class="cat-info">
                      <div class="cat-name"><u>{{ item.name }}</u></div>
                      <div class="cat-seller">{{ item.seller_business || item.seller_name }}</div>
                      <div class="cat-cost">
                        <span>Cost:</span> {{ formatNumber(item.price) }} FCFA
                      </div>
                      <div class="cat-meta">
                        <span class="cat-badge">In-Stock: {{ item.stock }}</span>
                        <span class="cat-badge">Type: {{ item.category || 'General' }}</span>
                      </div>
                    </div>

                    <div class="cat-footer">
                      <button
                        v-if="!item.is_reselling"
                        class="cat-btn"
                        :disabled="!!addingMap[item.id]"
                        @click.stop="addToCatalog(item)"
                      >
                        <i class="fas fa-plus"></i>
                        {{ addingMap[item.id] ? 'Adding…' : 'Resell' }}
                      </button>
                      <div v-else class="cat-added-row">
                        <button class="share-btn wa" title="Share via WhatsApp" @click.stop="shareWhatsApp(item)">
                          <i class="fab fa-whatsapp"></i>
                        </button>
                        <button class="share-btn fb" title="Share via Facebook" @click.stop="shareFacebook(item)">
                          <i class="fab fa-facebook-f"></i>
                        </button>
                        <button class="share-btn sh" title="Share via system" @click.stop="shareNative(item)">
                          <i class="fas fa-share-alt"></i>
                        </button>
                        <span class="cat-added">
                          <i class="fas fa-check"></i> Added
                        </span>
                      </div>
                    </div>
                  </a>
                </div>
              </div>

              <div v-if="!filteredCatalog.length && !catalogLoading" class="empty-state">
                <v-icon size="36" color="grey">mdi-storefront-outline</v-icon>
                <div class="empty-title">No products available</div>
                <div class="empty-subtitle">Sellers have not listed products yet.</div>
              </div>
            </v-card>
          </div>
        </v-tab-item>

        <!-- Mon catalogue -->
        <v-tab-item id="mine" value="mine">
          <div class="catalog-section">
            <v-card class="table-card">
              <v-card-title class="table-title">
                <div>
                  <div class="title-text">My Reseller Catalog</div>
                  <div class="subtitle-text">({{ myProducts.length }}) products you resell</div>
                </div>
              </v-card-title>

              <v-alert v-if="mineError" type="error" dense class="mx-4 mt-2 mb-0">
                {{ mineError }}
              </v-alert>
              <v-progress-linear v-if="mineLoading" indeterminate color="indigo" height="3" />

              <div class="table-wrapper">
                <table class="seller-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Product</th>
                      <th>Seller</th>
                      <th>Price (XAF)</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="myProducts.length">
                    <tr v-for="item in myProducts" :key="item.id">
                      <td>
                        <v-img
                          :src="item.image || '/placeholder-product.png'"
                          max-width="52px"
                          max-height="52px"
                          contain
                          class="thumb"
                        />
                      </td>
                      <td class="font-weight-semibold">{{ item.name }}</td>
                      <td>
                        <span class="text-caption">{{ item.seller_business }}</span>
                      </td>
                      <td class="font-weight-bold teal--text text--darken-1">{{ formatNumber(item.resale_price) }}</td>
                      <td>
                        <v-chip x-small :color="item.is_active ? 'success' : 'warning'"
                          dark>{{ item.is_active ? 'Active' : 'Inactive' }}</v-chip>
                      </td>
                      <td>
                        <div class="table-actions">
                          <v-btn x-small text :color="item.is_active ? 'error' : 'success'"
                            @click="toggleActive(item)">
                            <v-icon left x-small>{{ item.is_active ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                            {{ item.is_active ? 'Pause' : 'Resume' }}
                          </v-btn>
                          <v-btn icon x-small color="success" title="Share via WhatsApp" @click="shareWhatsApp(item)">
                            <v-icon x-small>mdi-whatsapp</v-icon>
                          </v-btn>
                          <v-btn icon x-small color="indigo" title="Share via Facebook" @click="shareFacebook(item)">
                            <v-icon x-small>mdi-facebook</v-icon>
                          </v-btn>
                          <v-btn icon x-small color="grey" title="Share via system" @click="shareNative(item)">
                            <v-icon x-small>mdi-share-variant</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="!myProducts.length && !mineLoading" class="empty-state">
                  <v-icon size="36" color="grey">mdi-package-variant-closed</v-icon>
                  <div class="empty-title">No listings yet</div>
                  <div class="empty-subtitle">Pick products from the catalog to start reselling.</div>
                </div>
              </div>
            </v-card>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-container>

    <v-snackbar
      v-model="copiedSnackbar"
      :timeout="2000"
      color="success"
      dark
      bottom
    >
      {{ copiedMessage }}
      <v-btn text color="white" @click="copiedSnackbar = false">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ResellerProducts',

  data() {
    return {
      tab: 'catalog',
      search: '',
      catalog: [],
      myProducts: [],
      catalogLoading: false,
      catalogError: null,
      mineLoading: false,
      mineError: null,
      addingMap: {},
      copiedId: null,
      copiedSnackbar: false,
    }
  },

  computed: {
    copiedMessage() {
      const lang = (this.$translator && this.$translator.getLanguage && this.$translator.getLanguage()) || 'en'
      const messages = { en: 'Link copied!', fr: 'Lien copié !', es: '¡Enlace copiado!' }
      return messages[lang] || messages.en
    },
    filteredCatalog() {
      const q = (this.search || '').toLowerCase().trim()
      if (!q) return this.catalog
      return this.catalog.filter(p =>
        String(p.name || '').toLowerCase().includes(q) ||
        String(p.category || '').toLowerCase().includes(q) ||
        String(p.seller_business || '').toLowerCase().includes(q)
      )
    },
  },

  mounted() {
    document.title = 'Reseller | Products'
    this.loadAll()
  },

  methods: {
    async loadAll() {
      await Promise.all([this.loadCatalog(), this.loadMine()])
    },

    async loadCatalog() {
      this.catalogLoading = true
      this.catalogError = null
      try {
        const res = await axios.get('/api/v1/market_place/reseller/available-products/')
        this.catalog = res.data.products || []
      } catch (err) {
        this.catalog = []
        this.catalogError = err.response?.data?.error || 'Unable to load catalog.'
      } finally {
        this.catalogLoading = false
      }
    },

    async loadMine() {
      this.mineLoading = true
      this.mineError = null
      try {
        const res = await axios.get('/api/v1/market_place/reseller/products/')
        this.myProducts = res.data.products || []
      } catch (err) {
        this.myProducts = []
        this.mineError = err.response?.data?.error || 'Unable to load your catalog.'
      } finally {
        this.mineLoading = false
      }
    },

    async addToCatalog(item) {
      this.$set(this.addingMap, item.id, true)
      try {
        const res = await axios.post('/api/v1/market_place/reseller/products/add/', {
          product_id: item.id,
        })
        this.$store.commit('setSnackBarMessage', res.data.message || 'Product added to your catalog!')
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)
        await Promise.all([this.loadCatalog(), this.loadMine()])
      } catch (err) {
        this.$store.commit('setSnackBarMessage', err.response?.data?.error || 'Unable to add product.')
        this.$store.commit('setSnackBarColor', 'error')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.$set(this.addingMap, item.id, false)
      }
    },

    async toggleActive(item) {
      try {
        const res = await axios.post(
          `/api/v1/market_place/reseller/products/${item.id}/edit/`,
          { is_active: !item.is_active }
        )
        this.$store.commit('setSnackBarMessage', res.data.message || 'Status updated')
        this.$store.commit('setSnackBarColor', 'success')
        this.$store.commit('activateSnackBar', true)
        await this.loadMine()
      } catch (err) {
        this.$store.commit('setSnackBarMessage', err.response?.data?.error || 'Unable to update')
        this.$store.commit('setSnackBarColor', 'error')
        this.$store.commit('activateSnackBar', true)
      }
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '0'
      return new Intl.NumberFormat('en-US').format(parseFloat(value))
    },

    appLink(item) {
      const productId = item.product_id || item.id
      const resellerId = item.reseller_product_id || item.id
      return `https://myapp.pettycashglobal.com/market_place/items/${productId}?r=${resellerId}`
    },
    facebookShareLink(item) {
      const productId = item.product_id || item.id
      const resellerId = item.reseller_product_id || item.id
      return item.share_url || `https://api.pettycashglobal.com/share/product/${productId}/?r=${resellerId}`
    },
    shareLink(item) {
      return this.appLink(item)
    },
    shareText(item) {
      const price = this.formatNumber(item.resale_price || item.price || 0)
      const title = item.name || 'PettyCash product'
      return `*${title}*\nOnly *${price} FCFA* on PettyCash 🛍️\n\nBuy here:\n${this.shareLink(item)}`
    },
    async buildShareFile(item) {
      if (!item.share_image) return null
      try {
        const res = await fetch(item.share_image)
        if (!res.ok) return null
        const blob = await res.blob()
        return new File([blob], 'product.jpg', { type: blob.type || 'image/jpeg' })
      } catch (e) {
        return null
      }
    },
    async shareWhatsApp(item) {
      const text = this.shareText(item)
      const link = this.shareLink(item)
      try {
        const file = await this.buildShareFile(item)
        if (file && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          // Partager l'image en pièce jointe → WhatsApp reçoit l'image à télécharger
          await navigator.share({ files: [file], text, url: link })
          return
        }
      } catch (e) {
        if (e && e.name === 'AbortError') return
      }
      // Desktop / navigateur sans support fichier : lien texte wa.me
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer')
    },
    shareFacebook(item) {
      const url = encodeURIComponent(this.facebookShareLink(item))
      const quote = encodeURIComponent(this.shareText(item))
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, '_blank', 'noopener,noreferrer')
    },
    itemKey(item) {
      return item.reseller_product_id || item.product_id || item.id
    },
    async writeClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
      } catch (e) {
        const ta = document.createElement('textarea')
        ta.value = text
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
    },
    async shareNative(item) {
      const text = this.shareText(item)
      const link = this.shareLink(item)
      if (navigator.share) {
        try {
          const payload = { text, url: link }
          const file = await this.buildShareFile(item)
          if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
            payload.files = [file]
          }
          await navigator.share(payload)
          return
        } catch (e) {
          if (e && e.name === 'AbortError') return
        }
      }
      await this.writeClipboard(link)
      this.copiedId = this.itemKey(item)
      this.copiedSnackbar = true
      clearTimeout(this.copiedTimer)
      this.copiedTimer = setTimeout(() => { this.copiedId = null }, 1800)
    },
  },
}
</script>

<style scoped>
.reseller-products {
  min-height: 100vh;
  background:
    radial-gradient(1200px 600px at 90% -10%, rgba(63,81,181,0.15), transparent),
    linear-gradient(135deg, #f7f8fb 0%, #eef2f7 100%);
}

.hero {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  padding: 28px 24px;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 70%, #5c6bc0 140%);
  color: #fff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.25);
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: rgba(255,255,255,0.18);
  margin-bottom: 8px;
}

.hero-title {
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 6px;
}

.hero-subtitle {
  margin: 0;
  opacity: 0.9;
  max-width: 560px;
}

.hero-glow {
  position: absolute;
  right: -120px;
  top: -120px;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(92,107,192,0.5), transparent 70%);
  filter: blur(4px);
}

.catalog-tabs {
  margin-top: 20px;
  background: transparent !important;
}

.catalog-section {
  margin-top: 12px;
}

.table-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #eef1f6;
}

.table-wrapper {
  overflow-x: auto;
}

.seller-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.seller-table th,
.seller-table td {
  padding: 12px 14px;
  border-top: 1px solid #eef1f6;
  vertical-align: middle;
  text-align: left;
}

.seller-table thead th {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  background: #fafbff;
}

.seller-table tbody tr:hover {
  background: #f9fafb;
}

.table-title {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 8px;
}

.title-text {
  font-weight: 700;
}

.subtitle-text {
  font-size: 12px;
  color: #6b7280;
}

.search-field {
  max-width: 260px;
}

.catalog-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  padding: 12px;
}

@media (min-width: 600px) {
  .catalog-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 960px) {
  .catalog-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1264px) {
  .catalog-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.catalog-item {
  display: flex;
}

.cat-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.cat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 10px -5px rgba(0, 0, 0, 0.2), 0 16px 24px 2px rgba(0, 0, 0, 0.14), 0 6px 30px 5px rgba(0, 0, 0, 0.12);
}

.cat-media {
  position: relative;
  width: 100%;
  height: 320px;
  background: #f8f8f8;
  overflow: hidden;
}

.cat-media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease, filter 0.3s ease;
}

.cat-card:hover .cat-media img {
  transform: scale(1.08);
  filter: brightness(1.05);
}

.cat-zoom {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
  color: #fff;
  font-size: 42px;
}

.cat-card:hover .cat-zoom {
  opacity: 1;
}

.cat-stock {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 2;
}

.cat-info {
  flex: 1 1 auto;
  background: #fff;
  padding: 8px 12px 4px;
  text-align: left;
}

.cat-name {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
  margin: 0;
}

.cat-seller {
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cat-cost {
  font-size: 14px;
  margin-top: 8px;
}

.cat-cost span {
  font-weight: 700;
}

.cat-meta {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.cat-badge {
  font-size: 12px;
  color: #37474f;
}

.cat-footer {
  flex: 0 0 auto;
  padding: 10px 12px 12px;
  background: #fff;
}

.cat-btn {
  display: block;
  width: 100%;
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #5c6bc0, #3f51b5);
  cursor: pointer;
  transition: filter 0.2s ease;
}

.cat-btn:hover {
  filter: brightness(1.08);
}

.cat-btn:disabled {
  opacity: 0.7;
  cursor: default;
}

.cat-added-row {
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 8px;
  align-items: stretch;
}

.share-btn {
  width: 40px;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 0.2s ease, transform 0.2s ease;
}

.share-btn.wa {
  background: #25d366;
}

.share-btn.fb {
  background: #1877f2;
}

.share-btn.sh {
  background: #6366f1;
}

.share-btn:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

.cat-added {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #1b5e20;
  background: #e8f5e9;
}

@media (max-width: 600px) {
  .catalog-grid {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 8px;
  }

  .cat-media {
    height: 280px;
  }
}

@media (min-width: 1264px) {
  .cat-media {
    height: 340px;
  }
}

.thumb {
  border-radius: 8px;
  border: 1px solid #eef1f6;
}

.table-actions {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}

.empty-state {
  text-align: center;
  padding: 24px 12px;
  color: #6b7280;
}

.empty-title {
  margin-top: 8px;
  font-weight: 600;
  color: #111827;
}

.empty-subtitle {
  font-size: 12px;
  margin-top: 2px;
}

@media (max-width: 600px) {
  .hero-title {
    font-size: 22px;
  }
}
</style>